// Daily-dose ceiling guard + pediatric mg/kg validator.
// Parses per-ingredient mg from composition text so hidden ingredients inside
// combination brands (e.g. paracetamol inside Zerodol-P / Sinarest) are counted.

export function freqPerDay(frequency) {
  const f = String(frequency || "").trim().toLowerCase();
  if (!f) return null;
  if (/prn|sos|stat/.test(f)) return null;
  if (/^od\b|^hs\b|once daily|night/.test(f)) return 1;
  if (/^bd\b|twice/.test(f)) return 2;
  if (/^tds\b|^tid\b|thrice/.test(f)) return 3;
  if (/^qid\b/.test(f)) return 4;
  if (/weekly/.test(f)) return 1 / 7;
  return null;
}

// mg of ONE ingredient delivered per single dose of this row
export function ingredientMgPerDose(rx, ingRe) {
  const comp = String(rx.composition || "");
  const strength = String(rx.strength || "");
  const generic = String(rx.generic_name || "");
  const dose = String(rx.dose || "");
  const MG_RE = /(\d+(?:\.\d+)?)\s*mg(?:\s*(?:\/|per)\s*(\d+(?:\.\d+)?)\s*ml)?/i;
  let mg = null, mlBase = null;
  for (const seg of comp.split(/[+,]/)) {
    if (!ingRe.test(seg)) continue;
    const m = seg.match(MG_RE);
    if (m) { mg = parseFloat(m[1]); if (m[2]) mlBase = parseFloat(m[2]); }
    break;
  }
  if (mg == null && ingRe.test(`${generic} ${rx.brand_name || ""}`)) {
    const m = strength.match(MG_RE);
    if (m) { mg = parseFloat(m[1]); if (m[2]) mlBase = parseFloat(m[2]); }
  }
  if (mg == null) return null;
  const mlM = dose.match(/(\d+(?:\.\d+)?)\s*ml/i);
  if (mlM) return mlBase ? (mg / mlBase) * parseFloat(mlM[1]) : null;
  if (/mg/i.test(dose)) {
    const raw = dose.match(/(\d+(?:\.\d+)?)/);
    return raw ? parseFloat(raw[1]) : null;
  }
  const uM = dose.match(/(\d+(?:\.\d+)?)/);
  const units = uM ? parseFloat(uM[1]) : 1;
  if (units > 6) return null;
  return mg * units;
}

// ── Adult max daily-dose ceilings (mg/day). ped = mg/kg/day bands. ──
export const CEILINGS = [
  { key: "Paracetamol", re: /(paracetamol|acetaminophen)/i, max: 4000, elderlyMax: 3000, liverMax: 2000, ped: { max: 60, low: 30 } },
  { key: "Ibuprofen", re: /ibuprofen/i, max: 2400, ped: { max: 40, low: 15 } },
  { key: "Diclofenac", re: /diclofenac/i, max: 150 },
  { key: "Aceclofenac", re: /aceclofenac/i, max: 200 },
  { key: "Naproxen", re: /naproxen/i, max: 1000 },
  { key: "Etoricoxib", re: /etoricoxib/i, max: 120 },
  { key: "Ketorolac", re: /ketorolac/i, max: 40 },
  { key: "Nimesulide", re: /nimesulide/i, max: 200 },
  { key: "Tramadol", re: /tramadol/i, max: 400 },
  { key: "Metformin", re: /metformin/i, max: 2550, warnAt: 2000 },
  { key: "Ondansetron", re: /ondansetron/i, max: 24, ped: { max: 0.6 } },
  { key: "Domperidone", re: /domperidone/i, max: 30, ped: { max: 1 } },
  { key: "Gabapentin", re: /gabapentin/i, max: 3600 },
  { key: "Pregabalin", re: /pregabalin/i, max: 600 },
  { key: "Atorvastatin", re: /atorvastatin/i, max: 80 },
  { key: "Rosuvastatin", re: /rosuvastatin/i, max: 40 },
  { key: "Amlodipine", re: /amlodipine/i, max: 10 },
  { key: "Telmisartan", re: /telmisartan/i, max: 80 },
  { key: "Metoprolol", re: /metoprolol/i, max: 200 },
  { key: "Cetirizine", re: /(?<!levo)cetirizine/i, max: 10 },
  { key: "Levocetirizine", re: /levocetirizine/i, max: 10 },
  { key: "Fexofenadine", re: /fexofenadine/i, max: 360 },
  { key: "Pantoprazole", re: /pantoprazole/i, max: 80 },
  { key: "Azithromycin", re: /azithromycin/i, max: 500, ped: { max: 12, low: 8 } },
  { key: "Amoxicillin", re: /(amoxicillin|amoxycillin)/i, max: 3000, ped: { max: 90, low: 25 } },
  { key: "Cefixime", re: /cefixime/i, max: 400, ped: { max: 12, low: 6 } },
  { key: "Cefpodoxime", re: /cefpodoxime/i, max: 400, ped: { max: 12, low: 8 } },
  { key: "Cephalexin", re: /(cephalexin|cefalexin)/i, max: 4000, ped: { max: 100, low: 25 } },
  { key: "Prednisolone", re: /prednisolone/i, ped: { max: 2.5 } },
];

const isElderly = (ctx) => parseFloat(ctx?.age) >= 65;
const hasLiverDisease = (ctx) => /cirrhosis|alcoholic liver|hepatitis|fatty liver|liver impairment|\bmash\b|\bnash\b/i.test(ctx?.knownText || "");

function effectiveMax(ing, ctx) {
  let max = ing.max;
  let why = "";
  if (ing.liverMax && hasLiverDisease(ctx) && ing.liverMax < max) { max = ing.liverMax; why = " (liver disease)"; }
  else if (ing.elderlyMax && isElderly(ctx) && ing.elderlyMax < max) { max = ing.elderlyMax; why = " (age ≥ 65)"; }
  return { max, why };
}

// mg/day this row contributes for one ingredient (null if unparseable/PRN)
function rowDailyMg(rx, ing) {
  const per = ingredientMgPerDose(rx, ing.re);
  if (per == null) return null;
  const fpd = freqPerDay(rx.frequency);
  if (!fpd) return null;
  return per * fpd;
}

// Tap-time: does adding `rx` push any ingredient over its daily ceiling?
export function ceilingCheck(rx, existing, ctx) {
  const issues = [];
  for (const ing of CEILINGS) {
    if (!ing.max) continue;
    const newMg = rowDailyMg(rx, ing);
    if (newMg == null) continue;
    const contributors = [];
    let total = newMg;
    for (const m of existing || []) {
      const mg = rowDailyMg(m, ing);
      if (mg) { total += mg; contributors.push(m.brand_name || m.generic_name); }
    }
    const { max, why } = effectiveMax(ing, ctx);
    const warnAt = ing.warnAt || max;
    if (total <= warnAt) continue;
    const src = contributors.length ? ` — already getting ${ing.key} from: ${contributors.join(", ")}` : "";
    const severity = total > max * 1.25 ? "block" : "warn";
    issues.push({
      severity,
      text: `${severity === "block" ? "⛔" : "⚠"} ${ing.key} total ${Math.round(total)} mg/day exceeds max ${max} mg/day${why}${src}`,
    });
  }
  return issues;
}

// Whole-table audit (banner shape) — catches manual rows & combos together
export function ceilingAudit(rows, ctx) {
  const alerts = [];
  for (const ing of CEILINGS) {
    if (!ing.max) continue;
    let total = 0;
    const contributors = [];
    for (const m of rows || []) {
      const mg = rowDailyMg(m, ing);
      if (mg) { total += mg; contributors.push(m.brand_name || m.generic_name); }
    }
    const { max, why } = effectiveMax(ing, ctx);
    const warnAt = ing.warnAt || max;
    if (contributors.length < 1 || total <= warnAt) continue;
    alerts.push({
      level: total > max * 1.25 ? "critical" : "caution",
      msg: `${ing.key} adds up to ${Math.round(total)} mg/day (max ${max}${why}) across: ${contributors.join(" + ")}`,
    });
  }
  return alerts;
}

// ── Pediatric age-based contraindications ──
const PED_CONTRA = [
  { re: /aspirin|ecosprin|acetylsalicylic/i, under: 16, severity: "block", text: "Aspirin under 16 — Reye's syndrome risk" },
  { re: /(doxycycline|tetracycline|minocycline)/i, under: 8, severity: "block", text: "Tetracycline-class under 8 yrs — permanent tooth staining" },
  { re: /(ciprofloxacin|levofloxacin|ofloxacin|moxifloxacin|norfloxacin)/i, under: 18, severity: "warn", text: "Fluoroquinolone under 18 — cartilage toxicity; reserve for resistant infections" },
  { re: /codeine/i, under: 12, severity: "block", text: "Codeine under 12 — fatal respiratory depression risk (CYP2D6)" },
  { re: /tramadol/i, under: 12, severity: "block", text: "Tramadol contraindicated under 12" },
  { re: /nimesulide/i, under: 12, severity: "block", text: "Nimesulide banned under 12 in India — hepatotoxicity" },
  { re: /promethazine/i, under: 2, severity: "block", text: "Promethazine under 2 — fatal respiratory depression" },
  { re: /loperamide/i, under: 2, severity: "block", text: "Loperamide under 2 — ileus/CNS toxicity" },
];

// Pediatric validator: age contraindications + mg/kg/day band check
export function pediatricCheck(rx, ctx) {
  const issues = [];
  const age = parseFloat(ctx?.age);
  if (!age || age >= 18) return issues;
  const txt = `${rx?.generic_name || ""} ${rx?.composition || ""} ${rx?.brand_name || ""}`;
  for (const r of PED_CONTRA) {
    if (age < r.under && r.re.test(txt)) {
      issues.push({ severity: r.severity, text: `${r.severity === "block" ? "⛔" : "⚠"} Age ${Math.round(age)}: ${r.text}` });
    }
  }
  const wt = parseFloat(ctx?.weightKg);
  for (const ing of CEILINGS) {
    if (!ing.ped) continue;
    const daily = rowDailyMg(rx, ing);
    if (daily == null) {
      if (age < 12 && ing.re.test(txt) && ingredientMgPerDose(rx, ing.re) != null) {
        issues.push({ severity: "warn", text: `⚠ ${ing.key} in a child — could not compute mg/kg (check dose & frequency manually)` });
      }
      continue;
    }
    if (!wt || wt > 45) {
      if (age < 12) issues.push({ severity: "warn", text: `⚠ ${ing.key} in a ${Math.round(age)}-yr-old — record weight to verify mg/kg dosing` });
      continue;
    }
    const perKg = daily / wt;
    if (ing.ped.max && perKg > ing.ped.max) {
      const severity = perKg > ing.ped.max * 1.3 ? "block" : "warn";
      issues.push({
        severity,
        text: `${severity === "block" ? "⛔" : "⚠"} ${ing.key} ${perKg.toFixed(1)} mg/kg/day for ${wt} kg child — exceeds max ${ing.ped.max} mg/kg/day`,
      });
    } else if (ing.ped.low && perKg < ing.ped.low) {
      issues.push({ severity: "warn", text: `⚠ ${ing.key} ${perKg.toFixed(1)} mg/kg/day — below usual ${ing.ped.low} mg/kg/day (may be subtherapeutic)` });
    }
  }
  return issues;
}
